import { HttpBackend, HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, Injector, inject } from '@angular/core';
import { CurrentUser } from '@shared/models/current-user.model';
import Keycloak, { KeycloakLoginOptions } from 'keycloak-js';
import { BehaviorSubject, Observable, from, switchMap, tap } from 'rxjs';
import { environment } from '@env/environment';
import { UserFullInfoService } from '@core/services/user-full-info.service';
import { getCleanCurrentUrl, removeAuthErrorHashFromCurrentUrl } from './auth-url.utils';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private currentUserSubject = new BehaviorSubject<CurrentUser | null>(null);
  currentUser$ = this.currentUserSubject.asObservable();

  private isAuthenticatedSubject = new BehaviorSubject<boolean>(false);
  private currentUserLoadErrorSubject = new BehaviorSubject<boolean>(false);
  currentUserLoadError$ = this.currentUserLoadErrorSubject.asObservable();
  private currentUserLoadInProgress = false;
  private isRedirectingToLogin = false;
  private sessionExpiryTimerId: ReturnType<typeof setTimeout> | null = null;

  private keycloak = inject(Keycloak);
  private injector = inject(Injector);
  private httpBackend = inject(HttpBackend);
  private readonly httpClient = new HttpClient(this.httpBackend);

  constructor() {
    removeAuthErrorHashFromCurrentUrl();

    // Initialize subject based on Keycloak's current authenticated state (check-sso done during app init)
    try {
      this.isAuthenticatedSubject.next(!!this.keycloak?.authenticated);
    } catch {
      this.isAuthenticatedSubject.next(false);
    }

    this.registerKeycloakHandlers();
    this.tryLoadCurrentUser();
  }

  private registerKeycloakHandlers(): void {
    if (!this.keycloak) {
      return;
    }

    this.keycloak.onReady = (authenticated?: boolean) => {
      this.isAuthenticatedSubject.next(Boolean(authenticated ?? this.keycloak?.authenticated));
      this.resetUserLoadError();
      this.scheduleSessionExpiryRedirect();
      this.tryLoadCurrentUser();
    };

    this.keycloak.onAuthSuccess = () => {
      this.isAuthenticatedSubject.next(true);
      this.isRedirectingToLogin = false;
      this.resetUserLoadError();
      this.scheduleSessionExpiryRedirect();
      this.tryLoadCurrentUser();
    };

    this.keycloak.onAuthRefreshSuccess = () => {
      this.isAuthenticatedSubject.next(true);
      this.resetUserLoadError();
      this.scheduleSessionExpiryRedirect();
      this.tryLoadCurrentUser();
    };

    this.keycloak.onAuthRefreshError = () => {
      void this.handleSessionExpired();
    };

    this.keycloak.onAuthLogout = () => {
      this.isRedirectingToLogin = false;
      this.clearSessionExpiryTimer();
      this.isAuthenticatedSubject.next(false);
      this.clearCurrentUser();
      this.clearUserInfoCache();
      this.resetUserLoadError();
    };

    this.keycloak.onTokenExpired = () => {
      this.handleAccessTokenExpired();
    };
  }

  // getter useful for debugging from templates/components
  get keycloakInstance(): Keycloak | undefined {
    return this.keycloak;
  }

  /** Observable para usar en componentes si querés reaccionar al login/logout */
  get isAuthenticated$(): Observable<boolean> {
    return this.isAuthenticatedSubject.asObservable();
  }

  /** Versión síncrona, ideal para el guard */
  isLoggedIn(): boolean {
    return !!this.keycloak && !!this.keycloak.authenticated;
  }

  /** Access token actual (o null si no hay) */
  get accessToken(): string | null {
    return this.keycloak?.token ?? null;
  }

  /**
   * Login envolviendo a keycloak-js.
   */
  async login(redirectUri?: string, loginOptions?: KeycloakLoginOptions): Promise<void> {
    if (!this.keycloak) {
      return;
    }

    const options: KeycloakLoginOptions = {
      redirectUri: redirectUri ? this.cleanRedirectUri(redirectUri) : window.location.origin + '/',
      ...(loginOptions ?? {})
    } as KeycloakLoginOptions;

    this.redirectToLogin(options);
  }

  loadCurrentUser(): Observable<CurrentUser> {
    return from(this.ensureValidToken()).pipe(
      switchMap(token => {
        if (!token) {
          throw new Error('No se pudo obtener un token válido para cargar el usuario.');
        }

        const headers = new HttpHeaders({
          Authorization: `Bearer ${token}`
        });

        return this.httpClient.get<CurrentUser>(`${environment.apiUrl}/me`, { headers });
      }),
      tap((user: CurrentUser) => this.currentUserSubject.next(user))
    );
  }

  getCurrentUserSnapshot(): CurrentUser | null {
    return this.currentUserSubject.value;
  }

  clearCurrentUser(): void {
    this.currentUserLoadInProgress = false;
    this.currentUserSubject.next(null);
  }

  private resetUserLoadError(): void {
    this.currentUserLoadErrorSubject.next(false);
  }

  /** Logout envolviendo a keycloak-js */
  async logout(): Promise<void> {
    if (!this.keycloak) {
      this.isAuthenticatedSubject.next(false);
      this.clearCurrentUser();
      this.clearUserInfoCache();
      this.clearSessionExpiryTimer();
      return;
    }

    this.isRedirectingToLogin = false;
    this.clearSessionExpiryTimer();
    await this.keycloak.logout();
    this.isAuthenticatedSubject.next(false);
    this.clearCurrentUser();
    this.clearUserInfoCache();
  }

  /**
   * Refresca el token si está por expirar.
   * Devuelve el token válido o null si no se pudo refrescar.
   */
  async ensureValidToken(): Promise<string | null> {
    if (!this.keycloak) {
      this.isAuthenticatedSubject.next(false);
      return null;
    }

    if (!this.keycloak.token) {
      this.isAuthenticatedSubject.next(false);
      return null;
    }

    try {
      // refrescar si le quedan menos de 30 segundos
      await this.keycloak.updateToken(30);
      this.isAuthenticatedSubject.next(true);
      this.scheduleSessionExpiryRedirect();
      return this.keycloak.token ?? null;
    } catch (err) {
      console.error('Error refrescando token', err);
      this.isAuthenticatedSubject.next(false);
      return null;
    }
  }

  async handleSessionExpired(redirectUri: string = getCleanCurrentUrl()): Promise<void> {
    if (this.isRedirectingToLogin) {
      return;
    }

    this.isRedirectingToLogin = true;
    this.clearSessionExpiryTimer();
    this.isAuthenticatedSubject.next(false);
    this.clearCurrentUser();
    this.clearUserInfoCache();

    if (!this.keycloak) {
      return;
    }

    try {
      this.redirectToLogin({ redirectUri: this.cleanRedirectUri(redirectUri) });
    } catch (error) {
      this.isRedirectingToLogin = false;
      console.error('Error redirigiendo al login tras vencer la sesión', error);
    }
  }

  private handleAccessTokenExpired(): void {
    if (!this.keycloak?.refreshTokenParsed?.exp) {
      void this.handleSessionExpired();
      return;
    }

    const refreshTokenExpiresAtMs = this.keycloak.refreshTokenParsed.exp * 1000;
    if (refreshTokenExpiresAtMs <= Date.now()) {
      void this.handleSessionExpired();
      return;
    }

    this.scheduleSessionExpiryRedirect();
  }

  /** Ejemplo: obtener claims decodificados si querés leer roles, email, etc. */
  get decodedToken(): any | null {
    if (!this.keycloak || !this.keycloak.token) {
      return null;
    }

    const [, payload] = this.keycloak.token.split('.');
    try {
      return JSON.parse(atob(payload));
    } catch {
      return null;
    }
  }

  private tryLoadCurrentUser(): void {
    // La carga del usuario completo ahora la maneja UserFullInfoService
    // AuthService solo gestiona la autenticación (token de Keycloak)
    // No hacer nada aquí para evitar llamadas duplicadas a /me
  }

  private clearUserInfoCache(): void {
    try {
      this.injector.get(UserFullInfoService).clear();
    } catch {
      // UserFullInfoService may not be constructed yet.
    }
  }

  private scheduleSessionExpiryRedirect(): void {
    this.clearSessionExpiryTimer();

    if (!this.keycloak?.authenticated || !this.keycloak.refreshTokenParsed?.exp) {
      return;
    }

    const expiresAtMs = this.keycloak.refreshTokenParsed.exp * 1000;
    const delayMs = expiresAtMs - Date.now();

    if (delayMs <= 0) {
      void this.handleSessionExpired();
      return;
    }

    this.sessionExpiryTimerId = setTimeout(() => {
      void this.handleSessionExpired();
    }, delayMs);
  }

  private clearSessionExpiryTimer(): void {
    if (this.sessionExpiryTimerId === null) {
      return;
    }

    clearTimeout(this.sessionExpiryTimerId);
    this.sessionExpiryTimerId = null;
  }

  private cleanRedirectUri(redirectUri: string): string {
    try {
      const url = new URL(redirectUri, window.location.origin);
      url.hash = '';
      return url.toString();
    } catch {
      return getCleanCurrentUrl();
    }
  }

  private redirectToLogin(options: KeycloakLoginOptions): void {
    window.location.replace(this.keycloak.createLoginUrl(options));
  }
}
